import { Check } from "lucide-react";

const CIRCLE = {
  done: "bg-accent border-accent text-white",
  active: "bg-accent/10 border-accent text-accent ring-4 ring-accent/10 dark:bg-white/10 dark:border-white dark:text-white dark:ring-white/10",
  todo: "bg-surface border-separator text-muted dark:bg-white/5 dark:border-white/20",
};

const LABEL = {
  done: "text-foreground",
  active: "text-accent dark:text-white",
  todo: "text-muted",
};

function stateOf(step, activeStep, completedSteps) {
  if (step.id === activeStep) return "active";
  if (completedSteps.includes(step.id)) return "done";
  return "todo";
}

const StepperDesktop = ({
  steps = [],
  activeStep,
  completedSteps = [],
  onStepSelect,
  orientation = "vertical",
  className = "",
}) => {
  const activeIndex = steps.findIndex((s) => s.id === activeStep);

  const canSelect = (step, index) => index < activeIndex || completedSteps.includes(step.id);

  if (orientation === "horizontal") {
    return (
      <nav aria-label="Progress" className={`flex items-center gap-2 ${className}`}>
        {steps.map((step, index) => {
          const state = stateOf(step, activeStep, completedSteps);
          const selectable = canSelect(step, index);
          return (
            <div key={step.id} className="flex min-w-0 items-center gap-2">
              <button
                type="button"
                disabled={!selectable}
                aria-current={state === "active" ? "step" : undefined}
                onClick={() => selectable && onStepSelect?.(step.id)}
                className="flex min-w-0 items-center gap-2 disabled:cursor-default"
              >
                <span
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-bold transition-colors ${CIRCLE[state]}`}
                >
                  {state === "done" ? <Check size={14} strokeWidth={2.5} /> : index + 1}
                </span>
                <span className={`truncate text-sm font-semibold transition-colors ${LABEL[state]}`}>{step.label}</span>
              </button>
              {index < steps.length - 1 && (
                <span
                  className={`h-px w-8 shrink-0 ${
                    completedSteps.includes(step.id) ? "bg-accent/40" : "bg-separator"
                  }`}
                />
              )}
            </div>
          );
        })}
      </nav>
    );
  }

  return (
    <nav aria-label="Progress" className={className}>
      <ol className="flex flex-col gap-1">
        {steps.map((step, index) => {
          const state = stateOf(step, activeStep, completedSteps);
          const selectable = canSelect(step, index);
          const isLast = index === steps.length - 1;

          return (
            <li key={step.id} className="relative pb-5 last:pb-0">
              {!isLast && (
                <span
                  aria-hidden="true"
                  className={`absolute left-[15px] top-9 bottom-1 w-px ${
                    completedSteps.includes(step.id) ? "bg-accent/40" : "bg-separator"
                  }`}
                />
              )}

              <button
                type="button"
                disabled={!selectable}
                aria-current={state === "active" ? "step" : undefined}
                onClick={() => selectable && onStepSelect?.(step.id)}
                className="relative flex w-full items-start gap-3 text-left disabled:cursor-default"
              >
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-bold transition-colors ${CIRCLE[state]}`}
                >
                  {state === "done" ? <Check size={15} strokeWidth={2.5} /> : index + 1}
                </span>

                <span className="flex min-w-0 flex-col pt-1">
                  <span className={`text-sm font-semibold leading-none transition-colors ${LABEL[state]}`}>
                    {step.label}
                  </span>
                  {step.description && (
                    <span className="mt-1.5 text-xs leading-snug text-muted">{step.description}</span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default StepperDesktop;